import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {toast} from 'react-toastify';
import {CheckCircle} from '../../icons';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {Spinner} from '../../Spinner';
import {resolvePromptPlaceholders} from './panelFormatters';
import {BOOSTER_STEP_FEED, boosterPathFor} from '../ia_booster/boosterSteps';
import './RailCards.scss';

// El rotador de fallback: en los últimos días del mes el prompt que sirve es el del cierre, el
// resto del tiempo el del día a día (mismo umbral que `IAPreviewBox`, la card de la ficha clásica).
const MONTH_CLOSE_FROM_DAY = 25;

// Cuántos desbloqueos entran en la card del rail. El backend los manda TODOS ordenados por
// relevancia y en un cliente nuevo son seis (21/11/7/4/3/1 prompts): una lista así deja de ser "lo
// próximo que conviene hacer" y se vuelve un backlog que nadie lee. El rail es un resumen — la
// lista completa vive en la sección IA. Sin "ver más" a propósito: no hay nada que expandir acá.
const MAX_UNLOCKS = 3;

// Qué le falta al agente para responder mejor, en el idioma del usuario. La clave del backend
// (`ingredient`) NUNCA se muestra cruda: un ingrediente que no esté acá simplemente no se pinta —
// una fila que diga "docs_drive" no le dice nada a nadie y ensucia la card.
const UNLOCK_LABELS = {
    ga4: ['client_panel.ia_unlock_ga4', 'Conecta GA4'],
    ecommerce: ['client_panel.ia_unlock_ecommerce', 'Suma su tienda'],
    memoria_reuniones: ['client_panel.ia_unlock_meetings', 'Carga la reunión de kickoff'],
    docs_drive: ['client_panel.ia_unlock_docs', 'Conecta Documentos de Drive'],
    objetivos: ['client_panel.ia_unlock_goals', 'Define objetivos'],
    presupuestos: ['client_panel.ia_unlock_budgets', 'Carga presupuestos'],
    paid_media: ['client_panel.ia_unlock_paid_media', 'Conecta una cuenta de pauta'],
    cambios_plataformas: ['client_panel.ia_unlock_changes', 'Activa el registro de cambios'],
    // Economía del cliente: el backend sólo lo propone con el módulo. Sin default: la clave existe en
    // es/en y un default la escondería si faltara.
    margen: ['client_panel.ia.unlock.margen_missing'],
};

// Los canales comparten copy ("Conecta X") y sólo cambia el nombre propio, que no se traduce.
const UNLOCK_CHANNEL_NAMES = {
    google: 'Google Ads',
    meta: 'Meta',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
};

// A dónde lleva cada fila: el desbloqueo tiene que terminar en la pantalla donde SE RESUELVE, no en
// una explicación. Un ingrediente sin destino conocido no se muestra.
const UNLOCK_DESTINATIONS = {
    ga4: (id) => `/client/${id}/conections`,
    ecommerce: (id) => `/client/${id}/conections`,
    google: (id) => `/client/${id}/conections`,
    meta: (id) => `/client/${id}/conections`,
    tiktok: (id) => `/client/${id}/conections`,
    linkedin: (id) => `/client/${id}/conections`,
    paid_media: (id) => `/client/${id}/conections`,
    // La memoria se alimenta en el acto Alimenta del AI Booster, como lo resuelve la banda «Usa tu IA»
    // (`unlockKindFor` → `UNLOCK_FEED`). No `/memory/setup`: en la 2.1 abre la Memoria clásica
    // (card zcK00Emw), y el ingrediente se cumple con cualquier material, no sólo con una reunión.
    memoria_reuniones: (id) => boosterPathFor(BOOSTER_STEP_FEED, id),
    docs_drive: (id) => `/client/${id}/memory`,
    // Aterrizan en la vista de líneas YA FILTRADA en las que les falta el dato (card gtAL5axI):
    // el desbloqueo no deja al usuario frente a 78 filas, lo deja frente a las que resolver.
    objetivos: (id) => `/client/${id}/lines?filtro=sin-objetivo`,
    presupuestos: (id) => `/client/${id}/lines?filtro=sin-presupuesto`,
    cambios_plataformas: (id) => `/client/${id}/activity-log`,
    margen: (id) => `/client/${id}/economia`,
};

/** Momento del mes que gobierna el prompt de fallback (el payload manda, esto es el plan B). */
export function fallbackMoment(isNewClient, now = new Date()) {
    if (isNewClient) return 'cliente_nuevo';
    return now.getDate() >= MONTH_CLOSE_FROM_DAY ? 'cierre_mes' : 'dia_a_dia';
}

/**
 * Card "Tu IA con este cliente" (`panel.ai`): si el conector está enchufado, un prompt accionable
 * para el momento, y qué le falta al agente para responder mejor.
 *
 * El prompt sale del agregado (`ai.moment_prompt`), que lo elige por CONTEXTO (una alerta viva, un
 * desvío). Cuando el backend no tiene nada que decir manda `null` y la card cae al rotador por día
 * del mes de la biblioteca de prompts — el mismo de la ficha clásica. Ese fetch de fallback ocurre
 * SÓLO en ese caso: en régimen la card no pide nada.
 *
 * `clientName` es para el fallback: la biblioteca devuelve el texto CRUDO del catálogo y el rail
 * mostraba «Para «{cliente}» en {período}...» tal cual (smoke visual del 2026-07-30). El prompt del
 * payload ya viene resuelto por el backend.
 */
export function IaCard({data, loading, clientId, clientName, isNewClient = false}) {
    const {t} = useTranslation();
    const [fallbackPrompts, setFallbackPrompts] = useState(null);
    // Qué prompt tiene el texto desplegado. Se guarda el id, no un booleano: si el agregado cambia
    // de prompt (otra alerta), el nuevo arranca plegado sin un efecto que lo resetee.
    const [expandedPromptId, setExpandedPromptId] = useState(null);

    const payloadPrompt = data && data.moment_prompt;
    const needsFallback = !!data && !payloadPrompt;

    useEffect(() => {
        if (!needsFallback) return undefined;
        let cancelled = false;

        // La biblioteca NO viene con envoltorio {status}: es un 200 con `available_ingredients`.
        const url = `${API_URL}agent/prompt-library/?agency_client_id=${clientId}`;
        new APIClient(url).call('GET').then((response) => {
            if (cancelled) return;
            // Sin módulo o error: lista vacía ⇒ la caja del prompt no se muestra (no es un error de
            // pantalla, es que no hay prompt que ofrecer).
            setFallbackPrompts(response && response.available_ingredients ? (response.prompts || []) : []);
        });

        return () => {
            cancelled = true;
        };
    }, [needsFallback, clientId]);

    // Prompt normalizado a `{id, text}`: el del payload y el de la biblioteca tienen nombres
    // distintos para lo mismo, y el resto de la card no debería enterarse de cuál llegó.
    const prompt = useMemo(() => {
        if (payloadPrompt) {
            // De las dos modalidades se toma la de cartera, que fija el cliente por id. Es la
            // variante SEGURA: en un GPT de todos los clientes le ahorra al agente resolver el
            // nombre (un round-trip, y ambiguo si hay homónimos), y en un GPT de este cliente
            // sobra pero no molesta. Preguntarle al usuario cuál quiere sería fricción en una
            // acción que hace todos los días: la mejor decisión es la que no tiene que tomar.
            return {
                id: payloadPrompt.prompt_id,
                title: payloadPrompt.title,
                text: payloadPrompt.prompt_text_agency_wide || payloadPrompt.prompt_text,
            };
        }
        if (!fallbackPrompts || !fallbackPrompts.length) return null;

        const moment = fallbackMoment(isNewClient);
        // `lock.enabled === true` significa prompt HABILITADO (lock_for: enabled = not missing).
        const found = fallbackPrompts.find(
            (p) => (p.momentos || []).includes(moment) && (!p.lock || p.lock.enabled));
        if (!found) return null;
        // El texto de la biblioteca viene CRUDO (`{cliente}`, `{período}`): se resuelve acá, antes
        // de mostrarlo Y de copiarlo — un prompt con los placeholders a la vista no sirve para
        // pegar, que es todo el punto de la card.
        return {
            id: found.id,
            title: found.title && resolvePromptPlaceholders(found.title, {clientName}),
            text: resolvePromptPlaceholders(found.prompt_text, {clientName}),
        };
    }, [payloadPrompt, fallbackPrompts, isNewClient, clientName]);

    const handleCopy = () => {
        if (!prompt) return;
        navigator.clipboard.writeText(prompt.text).then(() => {
            toast.success(t('client_panel.ia_prompt_copied', 'Prompt copiado'));
            // El uso se registra sólo si la copia ocurrió de verdad (patrón `PromptLibraryCard`).
            new APIClient(`${API_URL}agent/prompt-library/${prompt.id}/used/`)
                .call('POST', {agency_client_id: clientId});
        }).catch(() => {
            toast.error(t('client_panel.ia_prompt_copy_error', 'No pudimos copiar el prompt'));
        });
    };

    // La caja muestra PARA QUÉ sirve el prompt (su título), no el prompt: el texto entero en el rail
    // era «un choclo que no leo» y deformaba la card (card RYWfdWaX). El texto real sigue a un clic
    // —se puede leer antes de copiarlo— y «Copiar» copia siempre el texto, nunca el título. Un
    // prompt sin título no tiene resumen que mostrar: ahí va el texto a la vista, como antes.
    const renderPrompt = () => {
        const summary = prompt.title;
        const expanded = !summary || expandedPromptId === prompt.id;
        return (
            <div className="pnl-prompt">
                <span className="pnl-prompt__label">
                    {t('client_panel.ia_prompt_of_moment', 'Prompt del momento')}
                </span>
                {summary && <p className="pnl-prompt__summary">{summary}</p>}
                {expanded && <p className="pnl-prompt__text">{prompt.text}</p>}
                <div className="pnl-prompt__actions">
                    <button type="button" className="pnl-btn pnl-btn--ghost pnl-btn--mini" onClick={handleCopy}>
                        {t('client_panel.ia_prompt_copy', 'Copiar prompt')}
                    </button>
                    {summary && (
                        <button type="button" className="pnl-prompt__toggle" aria-expanded={expanded}
                                onClick={() => setExpandedPromptId(expanded ? null : prompt.id)}>
                            {expanded
                                ? t('client_panel.ia_prompt_hide', 'Ocultar prompt')
                                : t('client_panel.ia_prompt_show', 'Ver prompt')}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const unlocks = (data && data.unlocks) || [];

    const renderUnlock = (unlock) => {
        const ingredient = unlock && unlock.ingredient;
        const destination = UNLOCK_DESTINATIONS[ingredient];
        if (!destination) return null;

        let label;
        if (UNLOCK_CHANNEL_NAMES[ingredient]) {
            label = t('client_panel.ia_unlock_channel', 'Conecta {{channel}}', {
                channel: UNLOCK_CHANNEL_NAMES[ingredient],
                interpolation: {escapeValue: false},
            });
        } else if (UNLOCK_LABELS[ingredient]) {
            label = t(UNLOCK_LABELS[ingredient][0], UNLOCK_LABELS[ingredient][1]);
        } else {
            return null;
        }

        // Desbloquear (prompts que HOY no se pueden usar) pesa más que mejorar: si hay desbloqueos
        // se cuentan esos, y recién si no hay se muestra cuántos mejora.
        const unlocksCount = Number(unlock.unlocks_count) || 0;
        const enhancesCount = Number(unlock.enhances_count) || 0;
        const count = unlocksCount > 0 ? unlocksCount : enhancesCount;
        if (!count) return null;

        const countLabel = unlocksCount > 0
            ? t(
                count === 1 ? 'client_panel.ia_unlocks_one' : 'client_panel.ia_unlocks_other',
                count === 1 ? 'desbloquea {{n}} prompt' : 'desbloquea {{n}} prompts',
                {n: count})
            : t(
                count === 1 ? 'client_panel.ia_enhances_one' : 'client_panel.ia_enhances_other',
                count === 1 ? 'mejora {{n}} prompt' : 'mejora {{n}} prompts',
                {n: count});

        return (
            <Link key={ingredient} className="pnl-unlock" to={destination(clientId)}>
                <span className="pnl-unlock__label">{label}</span>
                <span className={`pnl-unlock__count ${unlocksCount > 0 ? '' : '-soft'}`}>{countLabel}</span>
            </Link>
        );
    };

    if (loading) {
        return (
            <div className="box pnl-card">
                <div className="box-loader"><Spinner color="positive small"/></div>
            </div>
        );
    }
    if (!data) return null;

    // El corte va DESPUÉS de descartar los que no se pueden pintar (ingrediente desconocido, contador
    // en cero): si no, un ingrediente del futuro se comería uno de los tres lugares con una fila que
    // ni siquiera se dibuja.
    const unlockRows = unlocks.map(renderUnlock).filter(Boolean).slice(0, MAX_UNLOCKS);

    return (
        <div className="box pnl-card">
            <div className="pnl-card__head">
                <h2 className="pnl-card__title">{t('client_panel.ia_title', 'Tu IA con este cliente')}</h2>
                <span className="pnl-card__spacer"/>
                <Link className="pnl-card__link" to={`/client/${clientId}/ia`}>
                    {t('client_panel.ia_open', 'Abrir IA')} &rarr;
                </Link>
            </div>

            <div className="pnl-cnt">
                {data.connector_active ? (
                    <p className="pnl-ia__connector">
                        <CheckCircle size={15} weight="fill"/>
                        {t('client_panel.ia_connector_active', 'Conector de Dashbo activo')}
                    </p>
                ) : (
                    <Link className="pnl-ia__connect-link" to={`/client/${clientId}/ia`}>
                        {t('client_panel.ia_connector_connect', 'Conectar tu IA')} &rarr;
                    </Link>
                )}

                {prompt && renderPrompt()}

                {unlockRows.length > 0 && <div className="pnl-unlocks">{unlockRows}</div>}
            </div>
        </div>
    );
}
